import type { ChainType, Candle } from '../types/index.js';

// ─── Contract Address Resolver ───────────────────────────────────────────────

const SOLANA_ADDRESS_REGEX = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
const EVM_ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/;

export type ResolvedAddress =
  | { chain: 'solana'; address: string }
  | { chain: Extract<ChainType, 'ethereum' | 'bsc' | 'arbitrum' | 'base'>; address: string }
  | null;

/**
 * Auto-detects chain from a raw contract address string.
 * Solana: Base58, 32–44 chars
 * EVM: 0x-prefixed hex, 42 chars total
 */
export function resolveContractAddress(raw: string): ResolvedAddress {
  const trimmed = raw.trim();

  if (EVM_ADDRESS_REGEX.test(trimmed)) {
    // We can't tell ETH vs BSB vs ARB from address alone;
    // return ethereum as default — caller should pass chain context if known.
    return { chain: 'ethereum', address: trimmed.toLowerCase() };
  }

  if (SOLANA_ADDRESS_REGEX.test(trimmed)) {
    return { chain: 'solana', address: trimmed };
  }

  return null;
}

export function isSolanaAddress(address: string): boolean {
  return SOLANA_ADDRESS_REGEX.test(address.trim());
}

export function isEVMAddress(address: string): boolean {
  return EVM_ADDRESS_REGEX.test(address.trim());
}

// ─── Number Formatting ───────────────────────────────────────────────────────

/**
 * Format a price with appropriate decimal places.
 * Handles micro-cap tokens (<$0.01) all the way to BTC.
 */
/**
 * Decimal places to show for a price: 2 from $1 up, 4 from $0.01, and below
 * that enough for 4 significant digits (0.000009988 → 9), capped at 12.
 * Shared by the price text and the chart's price-axis tick spacing.
 */
export function pricePrecision(price: number): number {
  const p = Math.abs(price);
  if (!(p > 0) || p >= 1) return 2;
  if (p >= 0.01) return 4;
  return Math.min(12, 3 - Math.floor(Math.log10(p)));
}

export function formatPrice(price: number): string {
  if (price < 0) return '-' + formatPrice(-price);
  if (price >= 1_000) return price.toLocaleString('en-US', { maximumFractionDigits: 0 });
  // Fixed notation even for tiny memecoin prices: "0.000009988", not "9.9880e-6".
  return price.toFixed(pricePrecision(price));
}

export function formatPriceWithSymbol(price: number): string {
  return '$' + formatPrice(price);
}

export function formatChange(pct: number): string {
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
}

export function formatVolume(vol: number): string {
  if (vol >= 1_000_000_000) return `$${(vol / 1_000_000_000).toFixed(2)}B`;
  if (vol >= 1_000_000)     return `$${(vol / 1_000_000).toFixed(2)}M`;
  if (vol >= 1_000)         return `$${(vol / 1_000).toFixed(2)}K`;
  return `$${vol.toFixed(2)}`;
}

/** Compact USD market cap: $1.95T, $412.30B, $3.10M. */
export function formatMarketCap(usd: number): string {
  if (usd >= 1_000_000_000_000) return `$${(usd / 1_000_000_000_000).toFixed(2)}T`;
  return formatVolume(usd);
}

export function formatTimestamp(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function timeAgo(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000)  return `${Math.floor(diff / 1_000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

// ─── Candle Utilities ─────────────────────────────────────────────────────────

/**
 * Merge an incoming real-time tick into the current candle.
 * Mutates-in-place for performance in hot paths.
 */
export function mergeTick(candle: Candle, price: number, volume: number): Candle {
  return {
    ...candle,
    high:   Math.max(candle.high, price),
    low:    Math.min(candle.low, price),
    close:  price,
    volume: candle.volume + volume,
  };
}

/**
 * Open a new candle from a tick, aligned to the given interval boundary.
 */
export function openCandle(price: number, volume: number, intervalMs: number): Candle {
  const now = Date.now();
  const aligned = Math.floor(now / intervalMs) * intervalMs;
  return {
    time:   Math.floor(aligned / 1000), // seconds
    open:   price,
    high:   price,
    low:    price,
    close:  price,
    volume,
  };
}

// ─── ID Generation ────────────────────────────────────────────────────────────

export function generateId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

// ─── Clamp / Math Helpers ─────────────────────────────────────────────────────

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

// ─── Position Sizing ──────────────────────────────────────────────────────────

/**
 * Calculate position size in USD given account equity, risk %, entry and stop loss.
 * Risk amount = equity × riskPct / 100
 * Position size = riskAmount / (|entry - stopLoss| / entry)
 */
export function calcPositionSize(
  accountEquity: number,
  riskPct:       number,
  entryPrice:    number,
  stopLossPrice: number,
): number {
  if (entryPrice <= 0 || stopLossPrice <= 0) return 0;
  const riskAmount = accountEquity * (riskPct / 100);
  const slDistance = Math.abs(entryPrice - stopLossPrice) / entryPrice;
  return slDistance > 0 ? riskAmount / slDistance : 0;
}

/**
 * Calculate TP price given entry, stop loss, and desired risk-reward ratio.
 */
export function calcTakeProfit(
  entryPrice:    number,
  stopLossPrice: number,
  side:          'buy' | 'sell',
  rrRatio:       number = 2,
): number {
  const distance = Math.abs(entryPrice - stopLossPrice) * rrRatio;
  return side === 'buy' ? entryPrice + distance : entryPrice - distance;
}
